import { useTranslation } from "react-i18next";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../ui/card";
import { useNavigate } from "react-router-dom";

interface Props {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
}

const FormWrapper = ({
  title,
  subtitle,
  children,
  footer,
  onSubmit,
  onCancel,
  submitText,
  cancelText,
  loading,
}: Props) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate(-1);
  };

  return (
    <Card className="h-full flex flex-col min-h-0">
      <CardHeader className="flex flex-row items-center gap-3">
        <button
          type="button"
          onClick={handleCancel}
          className="h-11 w-11 flex items-center justify-center rounded-sxs border border-border"
        >
          <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
        </button>
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{subtitle}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-scroll overflow-x-hidden min-h-0">
        {children}
      </CardContent>
      <CardFooter className="gap-2 justify-end mt-auto pt-2">
        {footer ?? (
          <>
            <Button
              type="reset"
              variant="outline"
              className="flex items-center gap-2 text-border-strong bg-transparent"
              onClick={handleCancel}
            >
              {cancelText ?? t("common:buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onSubmit}
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && <Loader2 size={20} className="animate-spin" />}
              {submitText ?? t("common:buttons.submit")}
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default FormWrapper;
